/**
 * Payment-method picker filter helpers for the donor widget.
 *
 * Spec contract:
 *   ref:tier-5-cross-screen/widget-payment-step
 *   ref:tier-5-cross-screen/widget-frequency-step
 *
 * Pure functions over the (paymentMethods, selectedFrequency)
 * pair — exported so the test suite can exercise the
 * disable-with-tooltip behavior without booting a full
 * DonationCheckout instance + DOM container.
 *
 * History:
 *   CHG-0956 / ITER-D1 — promoted the original hide-non-recurring
 *     filter (pre-D1 `.filter(m => m.supports_recurring)`) to a
 *     disable-with-tooltip pattern. The ALL-none case is still a
 *     cascade-level hide (frequency step disappears entirely per
 *     widget-frequency-step.cross-screen.yaml visibility rule),
 *     so by the time we reach this helper at least one method
 *     supports recurring.
 */

/** Minimal payment method shape consumed by the filter helpers. */
export interface PaymentMethodInput {
  provider_key: string;
  /** Donor-facing row TITLE — the org's name for the method when it set one. */
  display_name: string;
  /** The PLATFORM's label, ignoring the org's name. Drives the row's
   *  "Pay by …" line. See PaymentMethodEntry.default_display_name in ./api. */
  default_display_name?: string;
  supports_recurring: boolean;
  type?: string;
  /** Canonical mark filename from ADAPTER_METADATA.icon, delivered by
   *  /public/payment-methods. The row's icon resolves from this per
   *  ref:tier-5-experience/widget-payment-step picker_ux.rendering. Nullable:
   *  some providers (sandbox) declare no icon. */
  icon?: string | null;
}

/**
 * Second line of a picker row: "Pay by {platform label}", or '' to omit it.
 *
 * Spec: ref:tier-5-experience/widget-payment-step picker_ux.rendering. The two
 * donor-visible names are the ORG's (the title) and the PLATFORM's (this line).
 * Without this line an org's rename REPLACES the title outright, so a donor sees
 * only the org's wording — e.g. "Offline Payment" — with no way to tell which
 * instrument they are choosing.
 *
 * Returns '' when it would merely repeat the title, compared case-insensitively
 * on trimmed text. That is what gives an org which never renamed anything a
 * single-line row: the wizard prefills display_name with the platform catalog
 * label, so title and platform label collapse to the same text.
 *
 * `payByWord` is passed in so the caller can route it through the vocabulary
 * overlay; key `payment_method_pay_by`.
 */
export function payByLine(
  method: Pick<PaymentMethodInput, 'display_name' | 'default_display_name'>,
  payByWord: string,
): string {
  const platform = (method.default_display_name || '').trim();
  if (!platform) return '';
  const title = (method.display_name || '').trim();
  if (platform.toLowerCase() === title.toLowerCase()) return '';
  return `${payByWord} ${platform}`;
}

export interface MethodPickerEntry {
  method: PaymentMethodInput;
  disabled: boolean;
  /** Tooltip when disabled; empty string when enabled. */
  tooltip: string;
  /** Set true when the donor's current selectedPaymentMethod is
   *  this entry AND the entry is not disabled. Used by the
   *  template to apply the dk-active class. */
  active: boolean;
}

/**
 * Compute the rendered method-picker entries (visible row + disabled
 * flag + tooltip) for a given (paymentMethods, frequency,
 * selectedMethod) tuple.
 *
 * Filtering rules per ref:tier-5-cross-screen/widget-payment-step
 * method_picker.filter_rules:
 *
 *   hide-zero-channels — methods whose adapter doesn't include
 *     'widget' in supported_channels never reach donor surfaces.
 *     Today the widget already pre-filters at type !== 'manual';
 *     this helper accepts the already-filtered list.
 *
 *   disable-non-recurring-when-recurring-chosen — when
 *     selectedFrequency !== 'one-time' AND at least one method
 *     supports recurring, methods without supports_recurring are
 *     rendered as disabled rows with a tooltip pointing to the
 *     first recurring-capable method.
 *
 *   disable-recurring-only-when-one-time-chosen — symmetric
 *     no-op today (no adapter is recurring-only). Pinned for
 *     contract completeness; a future recurring-only adapter
 *     would land disabled here.
 */
export function computeMethodPickerEntries(
  methods: PaymentMethodInput[],
  selectedFrequency: string,
  selectedMethod: string | null,
): MethodPickerEntry[] {
  const isRecurring = selectedFrequency !== 'one-time';
  const firstRecurring = methods.find((m) => m.supports_recurring);

  return methods.map((m) => {
    const disabled =
      (isRecurring && !m.supports_recurring) ||
      // Symmetric one-time case — currently a no-op because no
      // adapter is recurring-only. If a future adapter declared
      // supports_recurring=true but supports_one_time=false, this
      // branch would mark it disabled in the one-time flow. The
      // input shape doesn't carry supports_one_time today so the
      // condition can never trigger; pinning the contract.
      false;

    let tooltip = '';
    if (disabled && firstRecurring) {
      tooltip = `${m.display_name} doesn't support recurring donations on this org. Pick ${firstRecurring.display_name} or switch to a one-time donation.`;
    }

    const active = selectedMethod === m.provider_key && !disabled;
    return { method: m, disabled, tooltip, active };
  });
}

/**
 * Auto-correct a selectedPaymentMethod when the donor flips
 * frequency to recurring and the previously-selected method
 * doesn't support recurring.
 *
 * Returns the corrected provider_key, or null if no recurring-
 * capable method exists (caller should be unreachable in this
 * case — the cascade hides the frequency step).
 */
export function autoCorrectSelectionForRecurring(
  methods: PaymentMethodInput[],
  selectedFrequency: string,
  selectedMethod: string | null,
): string | null {
  if (selectedFrequency === 'one-time' || !selectedMethod) {
    return selectedMethod;
  }
  const selected = methods.find((m) => m.provider_key === selectedMethod);
  if (!selected) return selectedMethod;
  if (selected.supports_recurring) return selectedMethod;
  const firstRecurring = methods.find((m) => m.supports_recurring);
  return firstRecurring ? firstRecurring.provider_key : selectedMethod;
}
